import { useState } from 'react'
import {
  DEPLOYMENT_OUTPUT_FIELDS,
  hasWithheldOutputs,
  outputLink,
  stateLocation,
} from '@qim/shared'
import { ApiError, fetchDeploymentOutputs } from '../api.js'
import { formatInstant } from '../format.js'
import type { DeploymentOutputs, StateLocation } from '@qim/shared'
import type { JSX } from 'react'

/**
 * A deployment's endpoints and identifiers.
 *
 * ## Why there is a button
 *
 * Reading these is a cross-account call into the customer's account, so it does
 * not happen on page load. A deployment that has been read before carries the
 * answer on its record and renders immediately; the button is the explicit
 * refresh. That is also the shape the account health check settled on, for the
 * same reason.
 *
 * Note the button re-reads the *last run's* log. Getting endpoints newer than
 * that means running `output` again — and never an apply, which is the
 * criterion. That run is started from the customer or deployment screen's run
 * form, which offered no `output` command at all until IM-74, leaving this
 * screen able to re-read a stale log but never to refresh it.
 *
 * Both acts used to be spelled "Refresh", which made the cheap one look like
 * the one that produces new endpoints. They are named apart now (IM-107): this
 * button says it reads the last run's log, and the line beneath it names the
 * command that writes a newer one. The environment URL no longer waits on any
 * of this — `InstanceHead` renders it from the record.
 *
 * ## Why nothing can be revealed
 *
 * Six of these are secrets. They are masked in the customer's account before the
 * line is written, and the control plane refuses to record them a second time —
 * so there is no stored value here to reveal, by construction. The row says
 * where the value lives instead of offering a button that cannot work.
 *
 * ## Why the note underneath
 *
 * "Held in the customer's account" is true and, by itself, a dead end: it says
 * where a value is *not*. The question it leaves — how does the customer get
 * their API key — was answered by whoever happened to remember, and the two
 * obvious guesses are both wrong. Their own run log does not hold one either,
 * because redaction runs before the line is written; and Qrvey cannot fetch one,
 * because the diagnostics role is denied `s3:GetObject` on that bucket. So the
 * panel names the state object and the command that reads it, once, under the
 * list rather than on each of nine rows.
 */
export const Outputs = ({
  baseUrl,
  tenantId,
  deploymentId,
  accountKey,
  region,
  initial,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly deploymentId: string
  readonly accountKey: string
  /** Absent on a deployment old enough that no run recorded one. */
  readonly region?: string
  readonly initial?: DeploymentOutputs
}): JSX.Element => {
  const [outputs, setOutputs] = useState<DeploymentOutputs | undefined>(initial)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [reading, setReading] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  // Derived, not fetched: the account and region are already on the record this
  // screen rendered from.
  const location = stateLocation({ accountKey, deploymentId, region })

  const read = async (): Promise<void> => {
    setReading(true)
    setFailure(undefined)
    setNote(undefined)
    try {
      const result = await fetchDeploymentOutputs(baseUrl, tenantId, deploymentId)
      setOutputs(result.outputs ?? undefined)
      setNote(result.note)
    } catch (error: unknown) {
      // The failure is usually ours or a lapsed grant, never the customer's
      // fault, so it reads as an inability rather than a fault in their account.
      setFailure(
        error instanceof ApiError
          ? `Could not read the endpoints just now (${error.status}).`
          : 'Could not read the endpoints just now.',
      )
    } finally {
      setReading(false)
    }
  }

  return (
    <section className="panel">
      <h2>Endpoints</h2>
      <p className="actions">
        {/* Ghost: reading endpoints changes nothing in the customer's account. */}
        <button type="button" className="ghost" onClick={() => void read()} disabled={reading}>
          {reading
            ? 'Reading…'
            : outputs === undefined
              ? 'Read the last run\u2019s log'
              : 'Re-read the last run\u2019s log'}
        </button>
        {outputs === undefined ? null : (
          <span className="muted">
            {' '}
            from run {outputs.fromRunId}, read{' '}
            <time dateTime={outputs.readAt}>{formatInstant(outputs.readAt)}</time>
          </span>
        )}
      </p>

      {/* The two are genuinely different acts and the panel used to blur them
        under one word. This reads a log that already exists in the customer's
        account; `Refresh endpoints` in the run panel dispatches the installer's
        `output` verb to write a new one. Only the second gets endpoints newer
        than the last run — which is the job IM-40 gave that command. */}
      <p className="hint">
        This reads the last run&rsquo;s log and changes nothing. For endpoints newer than that run,
        start <strong>Refresh endpoints</strong> in the run panel below.
      </p>

      {failure === undefined ? null : <p className="failure">{failure}</p>}
      {note === undefined ? null : <p className="muted">{note}</p>}

      {outputs === undefined ? (
        // Named rather than omitted: an absent section reads as "this deployment
        // has none", and the truth is that nobody has looked yet.
        note === undefined ? (
          <p className="muted">
            Nothing has been read for this deployment yet. The button above reads the last run
            &mdash; if that run printed no banner, <strong>Refresh endpoints</strong> in the run
            panel below produces one.
          </p>
        ) : null
      ) : (
        <dl className="detail">
          {DEPLOYMENT_OUTPUT_FIELDS.map((field) => {
            const value = outputs.values[field.key]
            if (value === undefined) return null
            const href = outputLink(field, value)
            return (
              <div key={field.key}>
                <dt>{field.label}</dt>
                <dd>
                  {value.state === 'present' ? (
                    href === undefined ? (
                      <code>{value.value}</code>
                    ) : (
                      // rel because these point into a customer's own estate.
                      <a href={href} target="_blank" rel="noreferrer noopener">
                        {value.value}
                      </a>
                    )
                  ) : value.state === 'withheld' ? (
                    <span className="muted">
                      Held in the customer&rsquo;s account &mdash; never copied here
                    </span>
                  ) : (
                    // A distinct word from "withheld": this deployment does not
                    // have one, which sends someone somewhere different.
                    <span className="muted">Not present in this deployment</span>
                  )}
                </dd>
              </div>
            )
          })}
        </dl>
      )}

      {hasWithheldOutputs(outputs) ? <WhereSecretsLive location={location} /> : null}
    </section>
  )
}

/**
 * Where the withheld values can be read from, and by whom.
 *
 * Rendered only when something on this deployment is actually withheld: on a
 * deployment whose every output is present it would be an answer to a question
 * nobody has.
 */
const WhereSecretsLive = ({
  location,
}: {
  readonly location: StateLocation | undefined
}): JSX.Element => (
  <div className="secret-location">
    <h3>Reading a withheld value</h3>
    <p className="hint">
      These exist only in the customer&rsquo;s own account, in this deployment&rsquo;s Terraform
      state. Their run log does not hold one either &mdash; the installer&rsquo;s output is redacted
      in their account before a line is written.
    </p>
    {location === undefined ? (
      // Named, not guessed. The bucket is per region, so without one there is no
      // address to give — and a URI that looks right and points nowhere costs
      // more than saying which fact is missing.
      <p className="hint">
        This deployment&rsquo;s region is not recorded, so the object cannot be named here. It is{' '}
        <code>{'{deploymentId}/state.tfstate'}</code> in the account&rsquo;s{' '}
        <code>qrvey-tfstate</code> bucket for whichever region it was deployed in.
      </p>
    ) : (
      <pre className="command">{`aws s3 cp ${location.uri} - | jq '.outputs'`}</pre>
    )}
    <p className="hint">
      Run it with the customer&rsquo;s own credentials. Qrvey&rsquo;s diagnostics role is denied{' '}
      <code>s3:GetObject</code> on that bucket by design, so this console cannot fetch a value and
      there is nothing stored here to reveal.
    </p>
  </div>
)
